import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  Dialog,
  DialogContent as MuiDialogContent,
  Typography,
  IconButton as MuiIconButton,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { RootState } from "../../../redux/store";
import useAppSelector from "../../../hooks/useAppSelector";
import { Bookmark } from "../../../types/app";
import BookmarkCardTitle from "./BookmarkCardTitle";
import { deleteBookmarkFromDB } from "../../../firebase/utils";
import { SnackbarFns, ViewFile } from "../../../utils/fnsRedux";
import { setBookmarkDeleteTracker } from "../../../redux/slices/bookmark";
import useAppDispatch from "../../../hooks/useAppDispatch";

const DialogContent = styled(MuiDialogContent)`
  padding-bottom: 20px;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const SourceText = styled(Typography)`
  font-size: 11px;
  font-style: italic;
  margin-top: 5px;
`;

const BookmarkContainer = styled.div`
  padding: 5px 10px;
  cursor: pointer;
  border-radius: 10px;
  &:hover {
    background-color: ${(props) => props.theme.sidebar.color};
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const DeleteButton = styled(MuiIconButton)`
  height: 25px;
  width: 25px;
  margin-left: 5px;
`;

interface Props {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const BookmarkGroupDialog = (props: Props) => {
  const { open, setOpen } = props;
  const handleSnackbar = SnackbarFns();
  const onClick = ViewFile();
  const dispatch = useAppDispatch();
  const bookmarkState = useAppSelector((state: RootState) => state.bookmark);
  const [disabled, setDisabled] = useState(false);
  const currBookmarks = bookmarkState.bookmarks.filter(
    (bookmark) =>
      bookmark.title === bookmarkState.currSelectedBookmarkTitle &&
      bookmark.group === bookmarkState.currSelectedBookmarkGroup
  );

  async function handleDelete(e: React.MouseEvent, bookmark: Bookmark) {
    setDisabled(true);
    e.stopPropagation();
    await deleteBookmarkFromDB(bookmark);
    handleSnackbar("success", "Bookmark deleted successfully", true);
    dispatch(setBookmarkDeleteTracker());
    setDisabled(false);
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <BookmarkCardTitle bookmark={currBookmarks[0]} />
        {currBookmarks.map((bookmark, idx) => {
          return (
            <BookmarkContainer
              key={idx}
              onClick={() =>
                onClick(bookmark.searchText, bookmark.pdfName, bookmark.page)
              }
            >
              <TitleContainer>
                <Typography fontWeight="bold">
                  {bookmark.answer ? bookmark.answer : `Bookmark ${idx + 1}`}
                </Typography>
                <DeleteButton
                  disabled={disabled}
                  onClick={(e) => handleDelete(e, bookmark)}
                >
                  <DeleteIcon sx={{ fontSize: "20px" }} />
                </DeleteButton>
              </TitleContainer>
              <Typography>{bookmark.displayText}</Typography>
              <SourceText>Source: {bookmark.pdfName}</SourceText>
            </BookmarkContainer>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

export default BookmarkGroupDialog;
